import { useState, useEffect } from 'react';
import Modal from '@/components/base/Modal';
import { supabase } from '@/lib/supabase';
import {
  ASSIGNMENTS_BUCKET,
  MAX_ASSIGNMENT_SIZE,
  isSupportedAssignmentFile,
  safeFileName,
} from '@/lib/fileDownload';
import type { Group, Lesson, Assignment } from '@/hooks/useDashboardData';

interface StudentRef {
  id: string;
  group_id: string | null;
}

interface AssignmentModalProps {
  open: boolean;
  onClose: () => void;
  onSaved: () => void;
  groups: Group[];
  lessons: Lesson[];
  students: StudentRef[];
  studentNames: Record<string, string>;
  assignment: Assignment | null;
}

function toIso(datetimeLocal: string): string | null {
  if (!datetimeLocal) return null;
  const d = new Date(datetimeLocal);
  return Number.isNaN(d.getTime()) ? null : d.toISOString();
}

function fromIso(iso: string | null): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  const pad = (n: number) => String(n).padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}T${pad(d.getHours())}:${pad(d.getMinutes())}`;
}

export default function AssignmentModal({
  open,
  onClose,
  onSaved,
  groups,
  lessons,
  students,
  studentNames,
  assignment,
}: AssignmentModalProps) {
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [groupId, setGroupId] = useState('');
  const [lessonId, setLessonId] = useState('');
  const [targetType, setTargetType] = useState<'grup' | 'ogrenci'>('grup');
  const [targetStudentIds, setTargetStudentIds] = useState<string[]>([]);
  const [selectedFile, setSelectedFile] = useState<File | null>(null);

  const isEdit = !!assignment;

  useEffect(() => {
    if (open) {
      setGroupId(assignment?.group_id ?? '');
      setLessonId(assignment?.lesson_id ?? '');
      setTargetType(assignment?.target_type === 'ogrenci' ? 'ogrenci' : 'grup');
      setTargetStudentIds(assignment?.target_students ?? []);
      setSelectedFile(null);
      setError(null);
      setSaving(false);
    }
  }, [open, assignment]);

  const filteredLessons = groupId ? lessons.filter((l) => l.group_id === groupId) : lessons;

  const groupStudents = students.filter((s) => s.group_id === groupId);

  function handleGroupChange(id: string) {
    setGroupId(id);
    // Grup değişince ders listesi filtre; seçili ders artık grupta değilse sıfırla
    if (lessonId) {
      const lesson = lessons.find((l) => l.id === lessonId);
      if (lesson && lesson.group_id !== id) setLessonId('');
    }
  }

  function handleLessonChange(id: string) {
    setLessonId(id);
    if (id) {
      const lesson = lessons.find((l) => l.id === id);
      if (lesson?.group_id) setGroupId(lesson.group_id);
    }
  }

  function toggleTargetStudent(id: string) {
    setTargetStudentIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id],
    );
  }

  async function handleSubmit(e: React.FormEvent<HTMLFormElement>) {
    e.preventDefault();
    const form = e.currentTarget;
    const fd = new FormData(form);

    const title = (fd.get('assignment_title') as string || '').trim();
    const deadlineRaw = (fd.get('deadline') as string || '').trim();
    const description = (fd.get('description') as string || '').trim();
    const required = (fd.get('required') as string || 'zorunlu').trim() === 'zorunlu';
    const reminder = fd.get('reminder') === 'evet';
    const link = (fd.get('link') as string || '').trim();
    const status = (fd.get('status') as string || 'gonderildi').trim();
    const file = selectedFile;

    if (!title) {
      setError('Lütfen ödev başlığını girin.');
      return;
    }
    if (!groupId) {
      setError('Lütfen bir grup seçin.');
      return;
    }
    if (targetType === 'ogrenci' && targetStudentIds.length === 0) {
      setError('En az bir öğrenci seçmelisiniz.');
      return;
    }
    if (!deadlineRaw) {
      setError('Lütfen son teslim tarihini belirleyin.');
      return;
    }

    const deadline = toIso(deadlineRaw);

    setError(null);
    setSaving(true);

    try {
      // Dosya yükleme ÖNCE yapılır; başarısız olursa ödev hiç kaydedilmez.
      let fileUrl: string | null = null;
      let hasFile = false;

      if (file && file.size > 0) {
        if (!isSupportedAssignmentFile(file)) {
          setError('Desteklenmeyen dosya türü. PDF, Word, PowerPoint, Excel veya görsel (JPG/PNG) yükleyebilirsiniz.');
          setSaving(false);
          return;
        }
        if (file.size > MAX_ASSIGNMENT_SIZE) {
          setError('Dosya boyutu 20 MB sınırını aşıyor. Lütfen daha küçük bir dosya seçin.');
          setSaving(false);
          return;
        }

        // Ödev id'si henüz yoksa güvenli bir klasör adı üret (temp uuid).
        const folderId = assignment?.id ?? crypto.randomUUID();
        const safeName = safeFileName(file.name);
        const path = `assignments/${folderId}/${Date.now()}-${safeName}`;
        const { error: uploadError } = await supabase.storage
          .from(ASSIGNMENTS_BUCKET)
          .upload(path, file);
        if (uploadError) {
          setError('Ödev dosyası yüklenemedi. Lütfen dosya türünü ve internet bağlantınızı kontrol edip tekrar deneyin.');
          setSaving(false);
          return;
        }
        const { data: publicUrl } = supabase.storage.from(ASSIGNMENTS_BUCKET).getPublicUrl(path);
        fileUrl = publicUrl.publicUrl;
        hasFile = true;
      } else if (isEdit) {
        // Düzenlemede yeni dosya seçilmediyse mevcut dosyayı koru.
        fileUrl = assignment?.file_url ?? null;
        hasFile = assignment?.has_file ?? false;
      }

      const payload = {
        title,
        description: description || null,
        lesson_id: lessonId || null,
        group_id: groupId,
        target_type: targetType,
        target_students: targetType === 'ogrenci' ? targetStudentIds : null,
        deadline,
        status,
        required,
        reminder,
        has_file: hasFile,
        file_url: fileUrl,
        link: link || null,
      };

      if (isEdit) {
        const { error: updateError } = await supabase
          .from('zehrahoca_assignments')
          .update(payload)
          .eq('id', assignment!.id);
        if (updateError) throw updateError;
      } else {
        const { error: insertError } = await supabase
          .from('zehrahoca_assignments')
          .insert({ ...payload, sent_date: new Date().toISOString() });
        if (insertError) throw insertError;
      }

      form.reset();
      setSelectedFile(null);
      onSaved();
      onClose();
    } catch (err) {
      setError((err as Error).message || 'Ödev kaydedilirken bir hata oluştu.');
    } finally {
      setSaving(false);
    }
  }

  return (
    <Modal
      open={open}
      onClose={onClose}
      title={isEdit ? 'Ödevi Düzenle' : 'Yeni Ödev Ekle'}
      subtitle={isEdit ? 'Ödev bilgilerini güncelleyin.' : 'Öğrencilere göndereceğiniz yeni bir ödev oluşturun.'}
    >
      <form onSubmit={handleSubmit} className="space-y-5">
        <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Ödev Başlığı</label>
            <input
              type="text"
              name="assignment_title"
              defaultValue={assignment?.title ?? ''}
              required
              placeholder="Örn: Üslü Sayılar Alıştırması"
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Grup <span className="text-red-500">*</span></label>
            <select
              value={groupId}
              onChange={(e) => handleGroupChange(e.target.value)}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              <option value="" disabled>Grup seçin</option>
              {groups.map((g) => (
                <option key={g.id} value={g.id}>{g.name}</option>
              ))}
            </select>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">İlgili Ders (opsiyonel)</label>
            <select
              value={lessonId}
              onChange={(e) => handleLessonChange(e.target.value)}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              <option value="">Ders seçin</option>
              {filteredLessons.map((l) => (
                <option key={l.id} value={l.id}>{l.title}</option>
              ))}
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Hedef</label>
            <div className="flex items-center gap-2 rounded-full bg-background-100 p-1">
              <button
                type="button"
                onClick={() => setTargetType('grup')}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition cursor-pointer ${
                  targetType === 'grup' ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-800'
                }`}
              >
                Tüm gruba gönder
              </button>
              <button
                type="button"
                onClick={() => setTargetType('ogrenci')}
                className={`whitespace-nowrap rounded-full px-4 py-2 text-sm font-semibold transition cursor-pointer ${
                  targetType === 'ogrenci' ? 'bg-white text-foreground-950 shadow-sm' : 'text-foreground-500 hover:text-foreground-800'
                }`}
              >
                Seçili öğrencilere gönder
              </button>
            </div>
          </div>

          {targetType === 'ogrenci' && (
            <div className="sm:col-span-2">
              <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Öğrenciler</label>
              {groupStudents.length === 0 ? (
                <p className="rounded-lg border border-dashed border-background-300 p-4 text-center text-xs text-foreground-500">
                  Bu grupta öğrenci bulunmuyor.
                </p>
              ) : (
                <div className="max-h-48 space-y-1 overflow-y-auto rounded-lg border border-background-200 p-2">
                  {groupStudents.map((s) => (
                    <label
                      key={s.id}
                      className="flex cursor-pointer items-center gap-2 rounded-lg px-2 py-1.5 text-sm text-foreground-700 transition hover:bg-background-50"
                    >
                      <input
                        type="checkbox"
                        checked={targetStudentIds.includes(s.id)}
                        onChange={() => toggleTargetStudent(s.id)}
                        className="h-4 w-4 rounded border-background-300 text-primary-500 focus:ring-primary-400"
                      />
                      {studentNames[s.id] ?? 'Öğrenci'}
                    </label>
                  ))}
                </div>
              )}
            </div>
          )}

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Son Teslim Tarihi</label>
            <input
              type="datetime-local"
              name="deadline"
              defaultValue={fromIso(assignment?.deadline ?? null)}
              required
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Durum</label>
            <select
              name="status"
              defaultValue={assignment?.status ?? 'gonderildi'}
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100 cursor-pointer"
            >
              <option value="gonderildi">Gönderildi</option>
              <option value="goruldu">Görüldü</option>
              <option value="teslim_edildi">Teslim Edildi</option>
              <option value="kontrol_edildi">Kontrol Edildi</option>
              <option value="eksik">Eksik</option>
              <option value="yeniden">Yeniden Yapılmalı</option>
            </select>
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Ödev Açıklaması</label>
            <textarea
              name="description"
              defaultValue={assignment?.description ?? ''}
              rows={3}
              maxLength={500}
              placeholder="Öğrencilerden beklenenler hakkında kısa bir açıklama..."
              className="w-full resize-none rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Ödev Dosyası Ekle (opsiyonel)</label>
            <div className="relative flex cursor-pointer items-center justify-center gap-2 rounded-lg border-2 border-dashed border-background-300 bg-background-50 px-4 py-6 text-sm text-foreground-600 transition hover:border-primary-300 hover:bg-primary-50">
              <i className="ri-upload-cloud-2-line text-xl" />
              {selectedFile ? (
                <span className="text-foreground-800 font-medium">{selectedFile.name} ({(selectedFile.size / (1024 * 1024)).toFixed(1)} MB)</span>
              ) : (
                <span>Dosya seçmek için tıklayın (PDF, Word, görsel...)</span>
              )}
              <input
                type="file"
                name="file"
                accept=".pdf,.doc,.docx,.ppt,.pptx,.xls,.xlsx,.png,.jpg,.jpeg"
                className="absolute inset-0 h-full w-full cursor-pointer opacity-0"
                onChange={(e) => setSelectedFile(e.target.files?.[0] ?? null)}
              />
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Ödev Zorunluluğu</label>
            <div className="flex items-center gap-4 rounded-lg border border-background-300 bg-white px-3.5 py-2.5">
              <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground-700">
                <input type="radio" name="required" value="zorunlu" defaultChecked={assignment ? assignment.required : true} className="accent-accent-500" /> Zorunlu
              </label>
              <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground-700">
                <input type="radio" name="required" value="opsiyonel" defaultChecked={assignment ? !assignment.required : false} className="accent-accent-500" /> Opsiyonel
              </label>
            </div>
          </div>

          <div>
            <label className="mb-1.5 block text-sm font-semibold text-foreground-800">Harici Link (opsiyonel)</label>
            <input
              type="url"
              name="link"
              defaultValue={assignment?.link ?? ''}
              placeholder="https://..."
              className="w-full rounded-lg border border-background-300 bg-white px-3.5 py-2.5 text-sm text-foreground-950 outline-none transition focus:border-primary-400 focus:ring-2 focus:ring-primary-100"
            />
          </div>

          <div className="sm:col-span-2">
            <label className="flex cursor-pointer items-center gap-2 text-sm text-foreground-700">
              <input
                type="checkbox"
                name="reminder"
                value="evet"
                defaultChecked={assignment?.reminder ?? false}
                className="h-4 w-4 accent-accent-500"
              />
              Son teslim tarihine yaklaşırken hatırlatma gönder
            </label>
          </div>
        </div>

        {error && (
          <div className="flex items-start gap-2 rounded-lg border border-red-200 bg-red-50 px-3.5 py-3 text-sm text-red-700">
            <i className="ri-error-warning-line mt-0.5 text-base" />
            <span>{error}</span>
          </div>
        )}

        <div className="flex items-center justify-end gap-3 border-t border-background-200 pt-5">
          <button
            type="button"
            onClick={onClose}
            className="rounded-lg px-4 py-2.5 text-sm font-semibold text-foreground-700 transition hover:bg-background-100 cursor-pointer whitespace-nowrap"
          >
            İptal
          </button>
          <button
            type="submit"
            disabled={saving}
            className="rounded-lg bg-primary-500 px-5 py-2.5 text-sm font-semibold text-white transition hover:bg-primary-600 disabled:opacity-70 cursor-pointer whitespace-nowrap"
          >
            {saving ? 'Kaydediliyor...' : isEdit ? 'Güncelle' : 'Ödevi Gönder'}
          </button>
        </div>
      </form>
    </Modal>
  );
}